import React, { useCallback, useEffect, useState } from 'react';
import { cn, Units } from '@ais-ui/utils';
import { useOpenLayersContext } from './OpenLayersContext';
import { transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';

import OLMap from 'ol/Map';

/**
 * Nice round values for scale bars in various units
 */
const NICE_SCALE_VALUES = {
  cm: [1, 2, 5, 10, 20, 50, 100],
  m: [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500],
  km: [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000],
  ft: [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500],
  mi: [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500],
  in: [1, 2, 5, 10, 20, 50],
  nm: [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000],
};

/**
 * Calculate the exact distance using the same method as the measurement tool
 * @param map OpenLayers map instance
 * @param pixelDistance The distance in pixels
 * @param dataProjection The data projection from context
 * @returns Distance in meters
 */
function calculateExactDistance(
  map: OLMap,
  pixelDistance: number,
  dataProjection: string
): number {
  if (!map) return 0;

  const view = map.getView();
  const center = view.getCenter();
  const resolution = view.getResolution();

  if (!center || !resolution) return 0;

  // Get projection
  const displayProjection = view.getProjection().getCode();

  // Calculate two points that are 'pixelDistance' pixels apart horizontally at the center
  const startPoint = [...center];
  const endPoint = [center[0] + resolution * pixelDistance, center[1]];

  // Transform to geographic coordinates - exactly as in the measurement tool
  const startPointGeo = transform(
    startPoint,
    displayProjection,
    dataProjection
  );
  const endPointGeo = transform(endPoint, displayProjection, dataProjection);

  // Calculate distance using the sphere formula - exactly as in the measurement tool
  return getDistance(startPointGeo, endPointGeo);
}

/**
 * Get the appropriate unit and conversion factor based on distance and user preference
 */
function getBestUnitForDistance(
  distanceInMeters: number,
  unitSystem: Units
): { unit: string; conversionFactor: number; niceValues: number[] } {
  switch (unitSystem) {
    case Units.METRIC:
      if (distanceInMeters < 1) {
        return {
          unit: 'cm',
          conversionFactor: 0.01,
          niceValues: NICE_SCALE_VALUES.cm,
        };
      } else if (distanceInMeters < 1000) {
        return {
          unit: 'm',
          conversionFactor: 1,
          niceValues: NICE_SCALE_VALUES.m,
        };
      } else {
        return {
          unit: 'km',
          conversionFactor: 1000,
          niceValues: NICE_SCALE_VALUES.km,
        };
      }

    case Units.IMPERIAL:
      const METERS_TO_FEET = 3.28084;
      const feet = distanceInMeters * METERS_TO_FEET;

      if (feet < 1) {
        return {
          unit: 'in',
          conversionFactor: 1 / (METERS_TO_FEET * 12),
          niceValues: NICE_SCALE_VALUES.in,
        };
      } else if (feet < 5280) {
        // Less than 1 mile
        return {
          unit: 'ft',
          conversionFactor: 1 / METERS_TO_FEET,
          niceValues: NICE_SCALE_VALUES.ft,
        };
      } else {
        return {
          unit: 'mi',
          conversionFactor: 1609.344,
          niceValues: NICE_SCALE_VALUES.mi,
        };
      }

    case Units.MARITIME:
      return {
        unit: 'nm',
        conversionFactor: 1852,
        niceValues: NICE_SCALE_VALUES.nm,
      };

    default:
      return {
        unit: 'm',
        conversionFactor: 1,
        niceValues: NICE_SCALE_VALUES.m,
      };
  }
}

/**
 * Finds a nice round scale value and calculates the corresponding width
 * @param map The OpenLayers map instance
 * @param maxWidth Maximum width in pixels for the scale bar
 * @param unitSystem The unit system preference from settings
 * @param dataProjection The data projection from context
 * @returns The calculated nice round value, corresponding width in pixels, and appropriate unit
 */
function findNiceScale(
  map: OLMap,
  maxWidth: number,
  unitSystem: Units,
  dataProjection: string
): { value: number; width: number; unit: string } {
  if (!map) {
    return { value: 0, width: maxWidth, unit: 'm' };
  }

  // Calculate the distance represented by maxWidth pixels
  const distanceInMeters = calculateExactDistance(
    map,
    maxWidth,
    dataProjection
  );

  // Get the best unit for this distance and unit system
  const { unit, conversionFactor, niceValues } = getBestUnitForDistance(
    distanceInMeters,
    unitSystem
  );

  // Convert distance to the selected unit
  const distanceInSelectedUnit = distanceInMeters / conversionFactor;

  // Find the largest nice value that fits within our distance
  let selectedValue = niceValues[0]; // Default to smallest value

  for (const value of niceValues) {
    if (value <= distanceInSelectedUnit) {
      selectedValue = value;
    } else {
      break;
    }
  }

  // Calculate the pixel width needed for this nice value
  // Using the ratio of selected value to total distance
  const pixelWidth = Math.round(
    maxWidth * (selectedValue / distanceInSelectedUnit)
  );

  // Ensure the pixel width doesn't exceed maxWidth
  const finalWidth = Math.min(pixelWidth, maxWidth);

  return {
    value: selectedValue,
    width: finalWidth,
    unit: unit,
  };
}

/**
 * Updates the scale based on the current map view
 */
function setupMapScaleListener(
  map: OLMap,
  setScaleValue: (value: number) => void,
  setScaleWidth: (width: number) => void,
  setScaleUnit: (unit: string) => void,
  unitSystem: Units,
  dataProjection: string,
  maxWidth = 200
): () => void {
  const updateScale = () => {
    const { value, width, unit } = findNiceScale(
      map,
      maxWidth,
      unitSystem,
      dataProjection
    );
    setScaleValue(value);
    setScaleWidth(width);
    setScaleUnit(unit);
  };

  // Update scale on map move or zoom
  map.getView().on('change:resolution', updateScale);
  map.on('moveend', updateScale);

  // Initial calculation
  updateScale();

  // Return cleanup function
  return () => {
    map.getView().un('change:resolution', updateScale);
    map.un('moveend', updateScale);
  };
}

interface MapScaleProps {
  unitSystem?: Units;
  maxWidth?: number;
  showDivisions?: boolean;
  className?: string;
  barClassName?: string;
  textClassName?: string;
  darkMode?: boolean;
  fixed?: boolean;
}

export function MapScale({
  unitSystem = Units.METRIC,
  maxWidth = 200,
  showDivisions = true,
  className,
  barClassName,
  textClassName,
  darkMode = false,
  fixed = false,
}: MapScaleProps) {
  // Reference to the actual scale line element for accurate measurement
  const scaleLineRef = React.useRef<HTMLDivElement>(null);
  const { map, dataProjection } = useOpenLayersContext();
  const [scaleValue, setScaleValue] = useState<number>(0);
  const [scaleWidth, setScaleWidth] = useState<number>(maxWidth);
  const [scaleUnit, setScaleUnit] = useState<string>('m');

  useEffect(() => {
    if (!map || fixed) return;

    // Setup listeners for map movement and cleanup when unmounted
    const cleanup = setupMapScaleListener(
      map,
      setScaleValue,
      setScaleWidth,
      setScaleUnit,
      unitSystem,
      dataProjection,
      maxWidth
    );

    return cleanup;
  }, [map, unitSystem, maxWidth, fixed, dataProjection]);

  // This effect ensures our calculations are based on the correct DOM element
  const measureScaleElement = useCallback(() => {
    // This is critical - we need to make sure we're measuring the actual scale line,
    // not the container that might have padding, borders, etc.
    if (scaleLineRef.current && map) {
      return {
        map,
        maxWidth,
        unitSystem,
        domElement: scaleLineRef.current,
      };
    }
    return null;
  }, [map, maxWidth, unitSystem]);

  // For fixed scales, calculate only once
  useEffect(() => {
    if (!map || !fixed) return;

    const { value, width, unit } = findNiceScale(
      map,
      maxWidth,
      unitSystem,
      dataProjection
    );
    setScaleValue(value);
    setScaleWidth(width);
    setScaleUnit(unit);
  }, [map, fixed, unitSystem, maxWidth, measureScaleElement, dataProjection]);

  // Format scale value for display (e.g., show 0.5 as 0.5, but 1.0 as 1)
  const formattedValue = scaleValue % 1 === 0 ? scaleValue : scaleValue;

  // Color scheme based on dark mode
  const colors = darkMode
    ? {
        bg: 'bg-background/80',
        text: 'text-foreground',
        border: 'border-border',
        bar: 'bg-muted-foreground',
      }
    : {
        bg: 'bg-card/80',
        text: 'text-foreground',
        border: 'border-border',
        bar: 'bg-foreground',
      };

  return (
    <div
      className={cn(
        'inline-flex flex-col items-center rounded-md p-2 shadow-sm',
        colors.bg,
        colors.text,
        'border',
        colors.border,
        className
      )}
    >
      <div className="flex flex-col items-center w-full">
        <div className="relative w-full">
          <div
            className={cn('h-2 relative', barClassName)}
            style={{ width: `${scaleWidth}px` }}
          >
            {/* Main bar - this is the actual scale line that represents the distance */}
            <div
              ref={scaleLineRef}
              className={cn('absolute inset-0 h-0.5 top-0', colors.bar)}
            />

            {/* End caps */}
            <div
              className={cn('absolute left-0 top-0 w-0.5 h-2', colors.bar)}
            />
            <div
              className={cn('absolute right-0 top-0 w-0.5 h-2', colors.bar)}
            />

            {/* Divisions */}
            {showDivisions && (
              <>
                <div
                  className={cn(
                    'absolute left-1/4 top-0 w-0.5 h-1.5',
                    colors.bar
                  )}
                />
                <div
                  className={cn(
                    'absolute left-1/2 top-0 w-0.5 h-1.5',
                    colors.bar
                  )}
                />
                <div
                  className={cn(
                    'absolute left-3/4 top-0 w-0.5 h-1.5',
                    colors.bar
                  )}
                />
              </>
            )}
          </div>
        </div>

        {/* Scale value */}
        <div
          className={cn(
            'text-xs mt-1 font-medium tracking-wide',
            textClassName
          )}
        >
          {formattedValue} {scaleUnit}
        </div>
      </div>
    </div>
  );
}
